import * as Hammer from "hammerjs"

import * as constants from "./constants"

export default class InteractionManager {

	constructor(player, rootElement) {
		this._player = player
		this._rootElement = rootElement

		// Create Hammer object to handle user gestures
		this._mc = new Hammer(this._rootElement)

		// Implement tap detection
		this._mc.on("tap", this._handleTap.bind(this, this._player))
	}

	adaptToStory(readingProgression, snapBehavior,
		goForward, goBackward, goToClosestSnapPoint, setScrollDelta) {
		this._storyData = {
			readingProgression,
			snapBehavior,
			goForward,
			goBackward,
			goToClosestSnapPoint,
			setScrollDelta,
		}

		// Implement swipe detection
		this._mc.get("swipe").set({
			direction: Hammer.DIRECTION_ALL,
			velocity: 10,
		})
		this._mc.on("swipeleft swiperight swipeup swipedown", this._handleSwipe.bind(this))

		// Implement scroll detection
		if (snapBehavior !== "discontinuous") {
			this._mc.get("pan").set({ direction: Hammer.DIRECTION_ALL })
			this._mc.on("panleft panright panup pandown panend", this._onMobileScroll.bind(this))

			// Do not implement mouse and trackpad scroll detection for now
			//this._rootElement.addEventListener("wheel", this._onWheelScroll.bind(this))
		}

		this._resetScroll()
	}

	_handleTap(player, e) {
		if (!this._storyData) {
			if (this._doOnCenterTap) {
				this._doOnCenterTap()
			}
			return
		}

		const { windowSize, viewportSize } = player
		const { width, height } = windowSize

		const { referencePercent } = constants
		let referenceXLength = (width - viewportSize.width) / 2
		referenceXLength += viewportSize.width * referencePercent
		let referenceYLength = (height - viewportSize.height) / 2
		referenceYLength += viewportSize.height * referencePercent

		const { x, y } = e.center
		const { readingProgression, goForward, goBackward } = this._storyData

		// Based on the readingProgression and where in the window the user has tapped,
		// decide whether the tap was a forward, center or backward tap
		switch (readingProgression) {
		case "ltr":
			if (x >= width - referenceXLength) {
				goForward()
			} else if (x <= referenceXLength) {
				goBackward()
			} else if (this._doOnCenterTap) {
				this._doOnCenterTap()
			}
			break
		case "rtl":
			if (x <= referenceXLength) {
				goForward()
			} else if (x >= width - referenceXLength) {
				goBackward()
			} else if (this._doOnCenterTap) {
				this._doOnCenterTap()
			}
			break
		case "ttb":
			if (y >= height - referenceYLength) {
				goForward()
			} else if (y <= referenceYLength) {
				goBackward()
			} else if (this._doOnCenterTap) {
				this._doOnCenterTap()
			}
			break
		case "btt":
			if (y <= referenceYLength) {
				goForward()
			} else if (y >= height - referenceYLength) {
				goBackward()
			} else if (this._doOnCenterTap) {
				this._doOnCenterTap()
			}
			break
		default:
			break
		}
	}

	_handleSwipe(e) {
		const { readingProgression, goForward, goBackward } = this._storyData
		switch (readingProgression) {
		case "ltr":
			if (e.type === "swipeleft") {
				goForward()
			} else if (e.type === "swiperight") {
				goBackward()
			}
			break
		case "rtl":
			if (e.type === "swiperight") {
				goForward()
			} else if (e.type === "swipeleft") {
				goBackward()
			}
			break
		case "ttb":
			if (e.type === "swipeup") {
				goForward()
			} else if (e.type === "swipedown") {
				goBackward()
			}
			break
		case "btt":
			if (e.type === "swipedown") {
				goForward()
			} else if (e.type === "swipeup") {
				goBackward()
			}
			break
		default:
			break
		}
	}

	_resetScroll() {
		this._lastScrollEvent = { deltaX: 0, deltaY: 0 }
	}

	// For touch-device scroll events
	_onMobileScroll(e) {
		if (e.type === "panend") {
			const { snapBehavior, goToClosestSnapPoint } = this._storyData
			switch (snapBehavior) {
			case "sticky":
				goToClosestSnapPoint()
				break
			default: // "discontinuous" or "free"
				this._releaseScroll(e)
				break
			}
			this._resetScroll()
		} else if (this._lastScrollEvent) {
			const absEvent = {
				deltaX: this._lastScrollEvent.deltaX - e.deltaX,
				deltaY: this._lastScrollEvent.deltaY - e.deltaY,
			}
			this._scroll(absEvent)
			this._lastScrollEvent = e
		}
	}

	// Record velocity and timestamp on drag end (i.e. scroll release)
	_releaseScroll(e) {
		const velocity = {
			x: e.velocityX * constants.velocityFactor,
			y: e.velocityY * constants.velocityFactor,
		}
		const releaseDate = Date.now()
		this._autoScroll(velocity, releaseDate)
	}

	// Apply kinetic scrolling formula after drag end (scroll release)
	_autoScroll(velocity, releaseDate) {
		const elapsedTime = Date.now() - releaseDate
		let deltaX = -velocity.x * Math.exp(-elapsedTime / constants.timeConstant)
		let deltaY = -velocity.y * Math.exp(-elapsedTime / constants.timeConstant)

		// Simple hack to allow for a smoother stop
		if (Math.abs(deltaX) < 1 || Math.abs(deltaY) < 1) {
			deltaX = Math.round(deltaX * 2) / 2
			deltaY = Math.round(deltaY * 2) / 2
		} else {
			deltaX = Math.round(deltaX)
			deltaY = Math.round(deltaY)
		}
		if (Math.abs(deltaX) > 0.5 || Math.abs(deltaY) > 0.5) {
			this._scroll({ deltaX, deltaY })
			requestAnimationFrame(this._autoScroll.bind(this, velocity, releaseDate))
		}
	}

	// Apply scroll to the current page via Story
	_scroll(e) {
		const { setScrollDelta } = this._storyData
		setScrollDelta({ deltaX: e.deltaX, deltaY: e.deltaY })
	}

	// For mouse and trackpad scroll events
	_onWheelScroll(e) {
		e.preventDefault()
		this._scroll(e)
	}

	setOnCenterTapAction(doOnCenterTap) {
		this._doOnCenterTap = doOnCenterTap
	}

	// Remove all event listeners on destroy
	destroy() {
		this._mc.off("tap", this._handleTap.bind(this, this._player))
		this._mc.off("swipeleft swiperight swipeup swipedown", this._handleSwipe.bind(this))
		this._mc.off("panleft panright panup pandown panend", this._onMobileScroll.bind(this))
	}

}